<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhatsappQrChannel from 'dashboard/api/channel/whatsappQrChannel';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const POLL_INTERVAL_MS = 5000;

const { t } = useI18n();

const connectionState = ref('');
const qrCodeBase64 = ref('');
const isVerifying = ref(false);
const isReconnecting = ref(false);
let pollTimer = null;

const isDisconnected = computed(
  () => connectionState.value && connectionState.value !== 'open'
);

const isBusy = computed(() => isVerifying.value || isReconnecting.value);

const buttonLabel = computed(() =>
  isDisconnected.value
    ? t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_RECONNECT')
    : t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_VERIFY')
);

const statusLabel = computed(() => {
  if (connectionState.value === 'open') {
    return t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_CONNECTED');
  }
  if (isDisconnected.value) {
    return t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_DISCONNECTED');
  }
  return '';
});

const clearPollTimer = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

const resetState = () => {
  clearPollTimer();
  connectionState.value = '';
  qrCodeBase64.value = '';
  isVerifying.value = false;
  isReconnecting.value = false;
};

const handleError = error => {
  const parsed = parseAPIErrorResponse(error);
  const message =
    (typeof parsed === 'string' && parsed) ||
    t('INBOX_MGMT.ADD.WHATSAPP_QR.ERROR_MESSAGE');
  useAlert(message);
};

const applyConnectionResult = data => {
  connectionState.value = data.state || '';
  if (data.state === 'open') {
    qrCodeBase64.value = '';
    clearPollTimer();
    return;
  }

  qrCodeBase64.value = data.qr_base64 || qrCodeBase64.value;
};

async function checkStatus() {
  try {
    const { data } = await WhatsappQrChannel.fetchStatus(props.inbox.id);
    applyConnectionResult(data);
  } catch (error) {
    handleError(error);
    clearPollTimer();
  }
}

const startPolling = () => {
  clearPollTimer();
  pollTimer = setInterval(checkStatus, POLL_INTERVAL_MS);
};

const verifyConnection = async () => {
  isVerifying.value = true;
  try {
    const { data } = await WhatsappQrChannel.fetchStatus(props.inbox.id);
    applyConnectionResult(data);
  } catch (error) {
    handleError(error);
  } finally {
    isVerifying.value = false;
  }
};

const reconnect = async () => {
  isReconnecting.value = true;
  try {
    const { data } = await WhatsappQrChannel.fetchQr(props.inbox.id);
    applyConnectionResult(data);
    if (data.state !== 'open') {
      startPolling();
    }
  } catch (error) {
    handleError(error);
    clearPollTimer();
  } finally {
    isReconnecting.value = false;
  }
};

const onAction = () => {
  if (isDisconnected.value) {
    reconnect();
    return;
  }
  verifyConnection();
};

watch(
  () => props.inbox.id,
  () => {
    resetState();
  }
);

onBeforeUnmount(() => {
  clearPollTimer();
});
</script>

<template>
  <SettingsFieldSection
    :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_CONNECTION_TITLE')"
    :help-text="
      $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_CONNECTION_SUBHEADER')
    "
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-wrap items-center gap-3">
        <NextButton
          type="button"
          size="md"
          :is-loading="isBusy"
          :disabled="isBusy"
          :label="buttonLabel"
          @click="onAction"
        />
        <p
          v-if="statusLabel"
          class="text-sm"
          :class="
            connectionState === 'open' ? 'text-n-teal-11' : 'text-n-ruby-11'
          "
        >
          {{ statusLabel }}
        </p>
      </div>

      <div v-if="qrCodeBase64" class="flex flex-col gap-3 max-w-xs">
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR.SCAN_INSTRUCTIONS') }}
        </p>
        <img
          :src="qrCodeBase64"
          :alt="$t('INBOX_MGMT.ADD.WHATSAPP_QR.QR_ALT')"
          class="w-64 h-64 rounded-lg border border-n-weak"
        />
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP_QR.WAITING') }}
        </p>
      </div>
    </div>
  </SettingsFieldSection>
</template>
